/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Product template.
 *
 * @namespace product
 */

import { formatMoney } from '@shopify/theme-currency';
import { register } from '@shopify/theme-sections';
import { getUrlWithVariant } from '@shopify/theme-product-form';
import Flickity from 'flickity';
import 'flickity-imagesloaded';
import 'flickity-fullscreen';
import { Modals } from '../components/modal';

/**
 * Product section constructor. Runs on page load as well as Theme Editor
 * `section:load` events.
 * @param {string} container - selector for the section container DOM element
 */

export default register('product', {
  onLoad() {
    // Look for a slideshow.
    const slideShow = document.querySelector('.product__slideshow');
    const productFormContainer = document.getElementById('ProductForm');
    const enterFullscreenButton = document.querySelector(
      '.btn--fullscreen-open'
    );
    const exitFullscreenButton = document.querySelector(
      '.btn--fullscreen-close'
    );

    this.previousButton = document.querySelector(
      '.controls-container--product .btn--control__prev'
    );
    this.nextButton = document.querySelector(
      '.controls-container--product .btn--control__next'
    );

    const carouselStatus = this.container.querySelector(
      '.product-slides-count'
    );

    // Initialise the slider
    this.slider = null;
    if (slideShow) {
      this.slider = new Flickity('.product__slideshow', {
        freeScroll: false,
        prevNextButtons: false,
        autoPlay: false,
        pageDots: true,
        imagesLoaded: true,
        setGallerySize: true,
        cellAlign: 'center',
        watchCSS: true,
        wrapAround: false,
        contain: true,
        fullscreen: true,
        on: {
          ready: function() {
            if (!this.previousButton) {
              return;
            }
            this.previousButton.disabled = true;
          }.bind(this)
        },
      });

      if (this.previousButton) {
        this.previousButton.addEventListener(
          'click',
          function() {
            this.slider.previous();
          }.bind(this)
        );
        this.nextButton.addEventListener(
          'click',
          function() {
            this.slider.next();
          }.bind(this)
        );
      }

      if (enterFullscreenButton) {
        enterFullscreenButton.addEventListener(
          'click',
          function() {
            this.slider.viewFullscreen();
            document.body.classList.add('is-fullscreen');
          }.bind(this)
        );
        exitFullscreenButton.addEventListener(
          'click',
          function() {
            this.slider.exitFullscreen();
            document.body.classList.remove('is-fullscreen');
          }.bind(this)
        );
      }

      window.productImageSlider = this.slider;
      if (productFormContainer && window.productForm) {
        window.productForm.global.updateImage(
          window.productForm.selectedVariantId
        );
      }

      const itemsCount = slideShow.dataset.items;
      let slideNumber = Number(this.slider.selectedIndex + 1);

      if (this.previousButton) {
        this.slider.on(
          'change',
          function(index) {
            if (index === 0) {
              this.previousButton.disabled = true;
            } else {
              this.previousButton.disabled = false;
            }

            if (index === this.slider.slides.length - 1) {
              this.nextButton.disabled = true;
            } else {
              this.nextButton.disabled = false;
            }
          }.bind(this)
        );
      }

      if (carouselStatus) {
        this.slider.on(
          'change',
          function(evt) {
            slideNumber = Number(this.slider.selectedIndex + 1);
            updateCounter(carouselStatus, slideNumber, itemsCount);
          }.bind(this)
        );

        updateCounter(carouselStatus, slideNumber, itemsCount);

        function updateCounter(counterEl, count, items) {
          counterEl.textContent = count + ' / ' + items;
        }
      }
    }

    Modals();

    document.addEventListener('variant:changed', function(evt) {
      const variant = evt.detail;
      const moneyFormat = window.theme.moneyFormat;

      // - Price
      if (window.theme.ecommerce === true) {
        const priceSelector = document.querySelector('[data-product-price]');
        const compareAtPriceSelector = document.querySelector(
          '[data-product-compare-at-price]'
        );
        const payLaterPriceSelector = document.querySelector('[data-product-price-pay-later]');
        compareAtPriceSelector.classList.add('visually-hidden');
        priceSelector.innerHTML = `<span class="money">${formatMoney(
          variant.price,
          moneyFormat
        )}</span>`;
        // - On Sale
        if (variant.price < variant.compare_at_price) {
          compareAtPriceSelector.innerHTML = `<span class="money">${formatMoney(
            variant.compare_at_price,
            moneyFormat
          )}</span>`;
          compareAtPriceSelector.classList.remove('visually-hidden');
        }
        // Pay Later
        if (payLaterPriceSelector) {
          payLaterPriceSelector.innerHTML = `<span class="money">${formatMoney(
            variant.price / 4,
            moneyFormat
          )}</span>`;
        }
      }

      const url = getUrlWithVariant(window.location.href, variant.id);
      window.history.replaceState({ path: url }, '', url);
    });

    const drawerDeliveryOpen = document.querySelector('[data-toggle-delivery]');
    if (drawerDeliveryOpen) {
      drawerDeliveryOpen.addEventListener(
        'click',
        this.btnDrawerDeliveryClickOpenHandler.bind(this)
      );
    }
    const drawerDeliveryClose = document.querySelector(
      '.drawer-delivery__close'
    );
    if (drawerDeliveryClose) {
      drawerDeliveryClose.addEventListener(
        'click',
        this.btnDrawerDeliveryClickCloseHandler.bind(this)
      );
    }

    const drawerDeliveryOverlay = document.querySelector(
      '[data-toggle-drawer-delivery]'
    );
    if (drawerDeliveryOverlay) {
      drawerDeliveryOverlay.addEventListener(
        'click',
        this.btnDrawerDeliveryClickCloseHandler.bind(this)
      );
    }

    const drawerLegalOpen = document.querySelector('[data-toggle-legal]');
    if (drawerLegalOpen) {
      drawerLegalOpen.addEventListener(
        'click',
        this.btnDrawerLegalClickOpenHandler.bind(this)
      );
    }
    const drawerLegalClose = document.querySelector('.drawer-legal__close');
    if (drawerLegalClose) {
      drawerLegalClose.addEventListener(
        'click',
        this.btnDrawerLegalClickCloseHandler.bind(this)
      );
    }

    const drawerLegalOverlay = document.querySelector(
      '[data-toggle-drawer-legal]'
    );
    if (drawerLegalOverlay) {
      drawerLegalOverlay.addEventListener(
        'click',
        this.btnDrawerLegalClickCloseHandler.bind(this)
      );
    }

    // Payments Drawer
    const drawerToggles = document.querySelectorAll('[data-toggle-payment-drawer]');
    if (drawerToggles) {
      drawerToggles.forEach(toggle => {
        toggle.addEventListener('click', () => {
          document.body.classList.toggle('drawer-payment-is-expanded');
        });
      });
    }

    this.productFeaturesSlider = null;
    const featuresSlider = document.querySelector('.product-features__slider');
    this.slideCount = 0;
    if (featuresSlider) {
      this.slideCount = featuresSlider.getAttribute('data-slide-count');
      this.productFeaturesSlider = new Flickity(featuresSlider, {
        freeScroll: false,
        prevNextButtons: false,
        autoPlay: false,
        pageDots: true,
        imagesLoaded: true,
        setGallerySize: true,
        cellAlign: 'center',
        watchCSS: true,
        wrapAround: false,
        contain: true,
      });
    }

    // POST commands to YouTube or Vimeo API
    function postMessageToPlayer(player, command) {
      if (player == null || command == null) {
        return;
      }
      player.contentWindow.postMessage(JSON.stringify(command), '*');
    }

    const playButton = this.container.querySelector('.btn--play');
    const videoImage = this.container.querySelector('.brand-video__image');
    const videoContainer = this.container.querySelector('.brand-video__video');

    if (videoContainer) {
      const player = videoContainer.querySelector('iframe');
      playButton.addEventListener('click', function() {
        videoImage.classList.add('visually-hidden');
        videoContainer.classList.remove('visually-hidden');
        player.src += '&autoplay=1';

        postMessageToPlayer(player, {
          method: 'play',
        });
      });
    }

    const trustSliderEl = document.querySelector(
      '#trust-indicators__slider--' + this.id
    );

    // Reduce slide count by one to align with the 0 positioned slide
    const trustSlidesCount = Number(this.container.dataset.slides - 1);
    this.trustIndicatorControllers = this.container.querySelector(
      '.controls-container--trust-indicators'
    );

    if (trustSliderEl) {
      var trustSlider = new Flickity(trustSliderEl, {
        freeScroll: false,
        autoplay: false,
        contain: true,
        prevNextButtons: false,
        pageDots: false,
        imagesLoaded: true,
        setGallerySize: true,
        cellAlign: 'left',
        watchCSS: true,
        wrapAround: true,
      });

      const previousButtonTrust = this.trustIndicatorControllers.querySelector(
        '.btn--control__prev'
      );
      previousButtonTrust.addEventListener('click', function() {
        trustSlider.previous();
      });

      const nextButtonTrust = this.trustIndicatorControllers.querySelector(
        '.btn--control__next'
      );
      nextButtonTrust.addEventListener('click', function() {
        trustSlider.next();
      });
    }
  },

  /**
   * Event callback for Theme Editor `section:unload` event
   */
  onUnload() {
    this.$container.off(this.namespace);
  },

  btnDrawerDeliveryClickOpenHandler(evt) {
    evt.preventDefault();
    document.body.classList.add('drawer-delivery-is-expanded');
  },

  btnDrawerDeliveryClickCloseHandler(evt) {
    evt.preventDefault();
    document.body.classList.remove('drawer-delivery-is-expanded');
  },

  btnDrawerLegalClickOpenHandler(evt) {
    evt.preventDefault();
    document.body.classList.add('drawer-legal-is-expanded');
  },

  btnDrawerLegalClickCloseHandler(evt) {
    evt.preventDefault();
    document.body.classList.remove('drawer-legal-is-expanded');
  },
});
